import React, { useState, useRef } from "react";
import { useSelector } from "react-redux";
import ReactMarkdown from "react-markdown";
import {
  FiZap, FiX, FiMessageCircle, FiGlobe, FiCpu,
  FiEdit3, FiChevronRight, FiLoader, FiCopy, FiCheck,
  FiBell, FiTrendingUp, FiLinkedin, FiFileText,
  FiUsers, FiTarget, FiBookOpen, FiSettings,
  FiMail, FiBarChart2, FiCalendar, FiAlertTriangle,
} from "react-icons/fi";
import { SERVER_URL } from "../../../databucket";

const LANGUAGES = [
  "Spanish", "French", "German", "Portuguese", "Chinese",
  "Japanese", "Korean", "Arabic", "Hindi", "Russian",
  "Italian", "Dutch", "Turkish", "Vietnamese", "Thai",
];

const TONES = [
  { key: "professional", label: "Professional", icon: "💼" },
  { key: "casual", label: "Casual", icon: "😊" },
  { key: "friendly", label: "Friendly", icon: "🤝" },
  { key: "formal", label: "Formal", icon: "📋" },
];

const REPORT_TYPES = [
  { key: "daily_summary", label: "Daily Summary", icon: "📋" },
  { key: "weekly_summary", label: "Weekly Summary", icon: "📊" },
  { key: "pipeline_status", label: "Pipeline Status", icon: "🎯" },
  { key: "team_performance", label: "Team Performance", icon: "👥" },
  { key: "client_engagement", label: "Client Engagement", icon: "🤝" },
];

const MEETING_TYPES = [
  { key: "client_review", label: "Client Review", icon: "🤝" },
  { key: "lead_strategy", label: "Lead Strategy", icon: "🎯" },
  { key: "pipeline_review", label: "Pipeline Review", icon: "📊" },
  { key: "team_standup", label: "Team Standup", icon: "👥" },
  { key: "one_on_one", label: "1:1 Meeting", icon: "💬" },
];

const LINKEDIN_TYPES = [
  { key: "connection_request", label: "Connection Request", icon: "🔗", charLimit: 300 },
  { key: "first_message", label: "First Message", icon: "💬", charLimit: 500 },
  { key: "inmail", label: "InMail", icon: "📧", charLimit: 1900 },
  { key: "follow_up", label: "Follow-Up", icon: "🔄", charLimit: 500 },
];

const FEATURE_CATEGORIES = [
  {
    title: "Chat Tools",
    features: [
      { key: "summarize", label: "Summarize Chat", desc: "Quick AI summary of the conversation", icon: FiCpu, bg: "#eef2ff", color: "#6366f1" },
      { key: "suggest", label: "Smart Replies", desc: "AI-generated reply suggestions", icon: FiMessageCircle, bg: "#fef3c7", color: "#d97706" },
      { key: "translate", label: "Translate", desc: "Translate text to any language", icon: FiGlobe, bg: "#dcfce7", color: "#16a34a" },
      { key: "rewrite", label: "Tone Rewrite", desc: "Rewrite text in a different tone", icon: FiEdit3, bg: "#fce7f3", color: "#db2777" },
    ],
  },
  {
    title: "Intelligence",
    features: [
      { key: "ask", label: "Ask AI", desc: "Ask anything about your dashboard data", icon: FiZap, bg: "#e0e7ff", color: "#4338ca" },
      { key: "alerts", label: "Smart Alerts", desc: "Overdue items, risks & pending approvals", icon: FiBell, bg: "#fef2f2", color: "#dc2626" },
      { key: "kpi", label: "My KPIs", desc: "Key performance metrics at a glance", icon: FiBarChart2, bg: "#f0fdf4", color: "#16a34a" },
      { key: "analytics", label: "Trend Analysis", desc: "Trends, benchmarks & anomalies", icon: FiTrendingUp, bg: "#fdf4ff", color: "#a855f7" },
    ],
  },
  {
    title: "Sales & Outreach",
    features: [
      { key: "email", label: "Draft Email", desc: "AI-powered email drafting", icon: FiMail, bg: "#eff6ff", color: "#3b82f6" },
      { key: "linkedin", label: "LinkedIn Message", desc: "Connection requests, InMails & sequences", icon: FiLinkedin, bg: "#eff6ff", color: "#0077b5" },
      { key: "sales", label: "Sales Coach", desc: "Deal strategy, scripts & objection handling", icon: FiTarget, bg: "#fff7ed", color: "#ea580c" },
      { key: "playbook", label: "BD Playbook", desc: "Search training content & best practices", icon: FiBookOpen, bg: "#fefce8", color: "#ca8a04" },
    ],
  },
  {
    title: "Reports & Prep",
    features: [
      { key: "report", label: "Generate Report", desc: "Daily, weekly, pipeline & team reports", icon: FiFileText, bg: "#f1f5f9", color: "#475569" },
      { key: "meeting", label: "Meeting Prep", desc: "Briefing, talking points & questions", icon: FiCalendar, bg: "#ecfdf5", color: "#059669" },
      { key: "ops", label: "Ops Guide", desc: "Insurance process guidance & checklists", icon: FiSettings, bg: "#fef3c7", color: "#b45309" },
    ],
  },
];

const AIAssistant = ({ open, onClose, conversationId, onInsertText }) => {
  const [activeSection, setActiveSection] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [askInput, setAskInput] = useState("");
  const [translateText, setTranslateText] = useState("");
  const [selectedLang, setSelectedLang] = useState("Spanish");
  const [rewriteText, setRewriteText] = useState("");
  const [copied, setCopied] = useState(false);
  // Phase 2-4 state
  const [emailPurpose, setEmailPurpose] = useState("");
  const [emailRecipient, setEmailRecipient] = useState("");
  const [linkedinRecipient, setLinkedinRecipient] = useState("");
  const [linkedinCompany, setLinkedinCompany] = useState("");
  const [linkedinType, setLinkedinType] = useState("connection_request");
  const [linkedinPurpose, setLinkedinPurpose] = useState("");
  const [reportType, setReportType] = useState("daily_summary");
  const [meetingType, setMeetingType] = useState("client_review");
  const [meetingEntity, setMeetingEntity] = useState("");
  const [meetingAgenda, setMeetingAgenda] = useState("");
  const [salesTopic, setSalesTopic] = useState("");
  const [opsTopic, setOpsTopic] = useState("");
  const [analyticsQuery, setAnalyticsQuery] = useState("");
  const resultRef = useRef(null);

  const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails);
  const msgState = useSelector((state) => state.chat.messages[conversationId]);
  const messages = msgState?.items || [];

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  };

  const getRecentMessages = (count = 30) =>
    messages.slice(-count).map((m) => ({
      senderName: m.senderName,
      content: m.content,
      type: m.type,
      createdAt: m.createdAt,
    }));

  const userName = loginUser?.firstname
    ? `${loginUser.firstname} ${loginUser.lastname || ""}`.trim()
    : "User";

  // ── Generic AI ask helper (routes through chat-ask → intent parser → all services) ──
  const askAI = async (question) => {
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch(`${SERVER_URL}/api/ai/chat-ask`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ question, messages: getRecentMessages(15) }),
      });
      const data = await res.json();
      setResult(data.answer || "No response received.");
    } catch {
      setResult("Failed to get AI response.");
    }
    setLoading(false);
  };

  // ── Chat Tools handlers ──
  const handleSummarize = async () => {
    setLoading(true);
    setResult(null);
    setActiveSection("summarize");
    try {
      const res = await fetch(`${SERVER_URL}/api/ai/chat-summarize`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ messages: getRecentMessages(50) }),
      });
      const data = await res.json();
      setResult(data.summary || "Could not generate summary.");
    } catch {
      setResult("Failed to connect to AI service.");
    }
    setLoading(false);
  };

  const handleSuggestReplies = async () => {
    setLoading(true);
    setSuggestions([]);
    setActiveSection("suggest");
    try {
      const res = await fetch(`${SERVER_URL}/api/ai/chat-suggest-reply`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ messages: getRecentMessages(10), userName }),
      });
      const data = await res.json();
      setSuggestions(data.suggestions || []);
    } catch {
      setSuggestions(["Got it!", "Thanks!", "Let me check."]);
    }
    setLoading(false);
  };

  const handleTranslate = async () => {
    if (!translateText.trim()) return;
    setLoading(true);
    setResult(null);
    setActiveSection("translate");
    try {
      const res = await fetch(`${SERVER_URL}/api/ai/chat-translate`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ text: translateText, targetLanguage: selectedLang }),
      });
      const data = await res.json();
      setResult(data.translation || "Translation failed.");
    } catch {
      setResult("Failed to translate.");
    }
    setLoading(false);
  };

  const handleToneRewrite = async (tone) => {
    if (!rewriteText.trim()) return;
    setLoading(true);
    setResult(null);
    setActiveSection("rewrite");
    try {
      const res = await fetch(`${SERVER_URL}/api/ai/chat-tone-rewrite`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ text: rewriteText, tone }),
      });
      const data = await res.json();
      setResult(data.rewritten || "Rewrite failed.");
    } catch {
      setResult("Failed to rewrite.");
    }
    setLoading(false);
  };

  // ── Intelligence handlers ──
  const handleAsk = async () => {
    if (!askInput.trim()) return;
    setActiveSection("ask");
    await askAI(askInput);
  };

  const handleAlerts = async () => {
    setActiveSection("alerts");
    await askAI("Show me my smart alerts. What needs my attention?");
  };

  const handleKPI = async () => {
    setActiveSection("kpi");
    await askAI("Show my KPI dashboard with key performance metrics.");
  };

  const handleAnalytics = async () => {
    if (!analyticsQuery.trim()) return;
    setActiveSection("analytics");
    await askAI(analyticsQuery);
  };

  // ── Sales & Outreach handlers ──
  const handleEmailDraft = async () => {
    if (!emailPurpose.trim()) return;
    setActiveSection("email");
    const question = emailRecipient.trim()
      ? `Draft an email to ${emailRecipient} about: ${emailPurpose}`
      : `Draft an email about: ${emailPurpose}`;
    await askAI(question);
  };

  const handleLinkedIn = async () => {
    if (!linkedinRecipient.trim()) return;
    setActiveSection("linkedin");
    const parts = [`Write a LinkedIn ${linkedinType.replace(/_/g, ' ')} for ${linkedinRecipient}`];
    if (linkedinCompany.trim()) parts.push(`at ${linkedinCompany}`);
    if (linkedinPurpose.trim()) parts.push(`about ${linkedinPurpose}`);
    await askAI(parts.join(' '));
  };

  const handleSalesCoach = async () => {
    if (!salesTopic.trim()) return;
    setActiveSection("sales");
    await askAI(salesTopic);
  };

  const handlePlaybook = async () => {
    if (!salesTopic.trim()) return;
    setActiveSection("playbook");
    await askAI(`Search playbook for: ${salesTopic}`);
  };

  // ── Reports & Prep handlers ──
  const handleReport = async (type) => {
    setActiveSection("report");
    const typeLabel = REPORT_TYPES.find(r => r.key === type)?.label || type;
    await askAI(`Generate my ${typeLabel} report.`);
  };

  const handleMeetingPrep = async () => {
    if (!meetingEntity.trim() && !meetingAgenda.trim()) return;
    setActiveSection("meeting");
    const parts = [`Prepare for my ${meetingType.replace(/_/g, ' ')} meeting`];
    if (meetingEntity.trim()) parts.push(`with ${meetingEntity}`);
    if (meetingAgenda.trim()) parts.push(`about: ${meetingAgenda}`);
    await askAI(parts.join(' '));
  };

  const handleOps = async () => {
    if (!opsTopic.trim()) return;
    setActiveSection("ops");
    await askAI(opsTopic);
  };

  // ── Utilities ──
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleInsert = (text) => {
    onInsertText?.(text);
    onClose();
  };

  const resetSection = () => {
    setActiveSection(null);
    setResult(null);
    setSuggestions([]);
  };

  if (!open) return null;

  // ── Shared result renderer ──
  const renderResult = (options = {}) => {
    const { showInsert = true, showCopy = true, loadingText = "Thinking..." } = options;
    return (
      <>
        {loading && (
          <div className="ai-panel__loading">
            <FiLoader className="ai-panel__spinner" size={24} />
            <span>{loadingText}</span>
          </div>
        )}
        {result && !loading && (
          <div className="ai-panel__result" ref={resultRef}>
            <div className="ai-panel__result-text"><ReactMarkdown>{result}</ReactMarkdown></div>
            <div className="ai-panel__result-actions">
              {showCopy && (
                <button onClick={() => handleCopy(result)} type="button" className="ai-panel__action-btn">
                  {copied ? <FiCheck size={14} /> : <FiCopy size={14} />}
                  {copied ? "Copied" : "Copy"}
                </button>
              )}
              {showInsert && (
                <button onClick={() => handleInsert(result)} type="button" className="ai-panel__action-btn ai-panel__action-btn--primary">
                  Insert
                </button>
              )}
            </div>
          </div>
        )}
      </>
    );
  };

  // ── Back button helper ──
  const renderBack = () => (
    <button className="ai-panel__back" onClick={resetSection} type="button">
      &larr; Back
    </button>
  );

  return (
    <div className="ai-panel">
      {/* Header */}
      <div className="ai-panel__header">
        <div className="ai-panel__header-title">
          <FiZap className="ai-panel__sparkle" size={18} />
          <span>AI Assistant</span>
        </div>
        <button className="ai-panel__close" onClick={onClose} type="button">
          <FiX size={18} />
        </button>
      </div>

      <div className="ai-panel__body">
        {/* ═══ Feature Cards Menu ═══ */}
        {!activeSection && (
          <div className="ai-panel__features">
            {FEATURE_CATEGORIES.map((cat) => (
              <div key={cat.title} className="ai-panel__category">
                <div className="ai-panel__category-title">{cat.title}</div>
                {cat.features.map((feat) => {
                  const Icon = feat.icon;
                  const clickHandler = () => {
                    // Features that need immediate action
                    if (feat.key === "summarize") return handleSummarize();
                    if (feat.key === "suggest") return handleSuggestReplies();
                    if (feat.key === "alerts") return handleAlerts();
                    if (feat.key === "kpi") return handleKPI();
                    // Features that open a form
                    setActiveSection(feat.key);
                  };
                  return (
                    <button key={feat.key} className="ai-panel__card" onClick={clickHandler} type="button">
                      <div className="ai-panel__card-icon" style={{ background: feat.bg, color: feat.color }}>
                        <Icon size={20} />
                      </div>
                      <div className="ai-panel__card-info">
                        <div className="ai-panel__card-title">{feat.label}</div>
                        <div className="ai-panel__card-desc">{feat.desc}</div>
                      </div>
                      <FiChevronRight size={16} className="ai-panel__card-arrow" />
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        {/* ═══ Summarize ═══ */}
        {activeSection === "summarize" && (
          <div className="ai-panel__section">
            {renderBack()}
            <div className="ai-panel__section-title"><FiCpu size={16} /> Conversation Summary</div>
            {renderResult({ showInsert: false, loadingText: "Analyzing conversation..." })}
          </div>
        )}

        {/* ═══ Smart Replies ═══ */}
        {activeSection === "suggest" && (
          <div className="ai-panel__section">
            {renderBack()}
            <div className="ai-panel__section-title"><FiMessageCircle size={16} /> Smart Reply Suggestions</div>
            {loading ? (
              <div className="ai-panel__loading">
                <FiLoader className="ai-panel__spinner" size={24} />
                <span>Generating suggestions...</span>
              </div>
            ) : suggestions.length > 0 ? (
              <div className="ai-panel__suggestions">
                {suggestions.map((s, i) => (
                  <button key={i} className="ai-panel__suggestion-chip" onClick={() => handleInsert(s)} type="button">
                    <span>{s}</span>
                    <FiChevronRight size={14} />
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        )}

        {/* ═══ Translate ═══ */}
        {activeSection === "translate" && (
          <div className="ai-panel__section">
            {renderBack()}
            <div className="ai-panel__section-title"><FiGlobe size={16} /> Translate</div>
            <textarea
              className="ai-panel__textarea"
              value={translateText}
              onChange={(e) => setTranslateText(e.target.value)}
              placeholder="Paste or type text to translate..."
              rows={3}
            />
            <div className="ai-panel__lang-grid">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang}
                  className={`ai-panel__lang-btn ${selectedLang === lang ? "ai-panel__lang-btn--active" : ""}`}
                  onClick={() => setSelectedLang(lang)}
                  type="button"
                >
                  {lang}
                </button>
              ))}
            </div>
            <button
              className="ai-panel__submit-btn"
              onClick={handleTranslate}
              disabled={loading || !translateText.trim()}
              type="button"
            >
              {loading ? <FiLoader className="ai-panel__spinner" size={14} /> : <FiGlobe size={14} />}
              Translate to {selectedLang}
            </button>
            {renderResult({ loadingText: "Translating..." })}
          </div>
        )}

        {/* ═══ Tone Rewrite ═══ */}
        {activeSection === "rewrite" && (
          <div className="ai-panel__section">
            {renderBack()}
            <div className="ai-panel__section-title"><FiEdit3 size={16} /> Tone Rewrite</div>
            <textarea
              className="ai-panel__textarea"
              value={rewriteText}
              onChange={(e) => setRewriteText(e.target.value)}
              placeholder="Type or paste text to rewrite..."
              rows={3}
            />
            <div className="ai-panel__tone-grid">
              {TONES.map((tone) => (
                <button
                  key={tone.key}
                  className="ai-panel__tone-btn"
                  onClick={() => handleToneRewrite(tone.key)}
                  disabled={loading || !rewriteText.trim()}
                  type="button"
                >
                  <span>{tone.icon}</span>
                  <span>{tone.label}</span>
                </button>
              ))}
            </div>
            {renderResult({ loadingText: "Rewriting..." })}
          </div>
        )}

        {/* ═══ Ask AI ═══ */}
        {activeSection === "ask" && (
          <div className="ai-panel__section">
            {renderBack()}
            <div className="ai-panel__section-title"><FiZap size={16} /> Ask AI</div>
            <div className="ai-panel__ask-form">
              <textarea
                className="ai-panel__textarea"
                value={askInput}
                onChange={(e) => setAskInput(e.target.value)}
                placeholder="Ask anything about your data, leads, requests, reports..."
                rows={3}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleAsk();
                  }
                }}
              />
              <button
                className="ai-panel__submit-btn"
                onClick={handleAsk}
                disabled={loading || !askInput.trim()}
                type="button"
              >
                {loading ? <FiLoader className="ai-panel__spinner" size={14} /> : <FiZap size={14} />}
                Ask AI
              </button>
            </div>
            <div className="ai-panel__quick-asks">
              {["Show my open leads", "Pending requests this week", "What's on my plate?", "Team attendance today"].map((q) => (
                <button key={q} type="button" className="ai-panel__quick-chip" onClick={() => { setAskInput(q); }}>
                  {q}
                </button>
              ))}
            </div>
            {renderResult({ loadingText: "Thinking..." })}
          </div>
        )}

        {/* ═══ Smart Alerts ═══ */}
        {activeSection === "alerts" && (
          <div className="ai-panel__section">
            {renderBack()}
            <div className="ai-panel__section-title"><FiBell size={16} /> Smart Alerts</div>
            <div className="ai-panel__alert-filters">
              {["All", "Leads", "Requests", "HR", "Activity"].map((cat) => (
                <button
                  key={cat}
                  type="button"
                  className="ai-panel__quick-chip"
                  onClick={() => {
                    const q = cat === "All"
                      ? "Show me my smart alerts. What needs my attention?"
                      : `Show me my ${cat.toLowerCase()} alerts.`;
                    askAI(q);
                  }}
                >
                  {cat}
                </button>
              ))}
            </div>
            {renderResult({ showInsert: false, loadingText: "Checking alerts..." })}
          </div>
        )}

        {/* ═══ KPI Dashboard ═══ */}
        {activeSection === "kpi" && (
          <div className="ai-panel__section">
            {renderBack()}
            <div className="ai-panel__section-title"><FiBarChart2 size={16} /> My KPIs</div>
            <div className="ai-panel__quick-asks">
              {["My KPIs", "Team KPIs", "Leads KPIs this month", "Requests KPIs this week"].map((q) => (
                <button key={q} type="button" className="ai-panel__quick-chip" onClick={() => askAI(q)}>
                  {q}
                </button>
              ))}
            </div>
            {renderResult({ showInsert: false, loadingText: "Loading KPIs..." })}
          </div>
        )}

        {/* ═══ Trend Analysis ═══ */}
        {activeSection === "analytics" && (
          <div className="ai-panel__section">
            {renderBack()}
            <div className="ai-panel__section-title"><FiTrendingUp size={16} /> Trend Analysis</div>
            <textarea
              className="ai-panel__textarea"
              value={analyticsQuery}
              onChange={(e) => setAnalyticsQuery(e.target.value)}
              placeholder="e.g., 'Lead conversion trend this quarter', 'Weekly request completion rate'..."
              rows={2}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleAnalytics();
                }
              }}
            />
            <button
              className="ai-panel__submit-btn"
              onClick={handleAnalytics}
              disabled={loading || !analyticsQuery.trim()}
              type="button"
            >
              {loading ? <FiLoader className="ai-panel__spinner" size={14} /> : <FiTrendingUp size={14} />}
              Analyze
            </button>
            <div className="ai-panel__quick-asks">
              {[
                "Lead conversion trend this quarter",
                "Monthly request completion rate",
                "Compare my performance to team",
                "Detect anomalies in lead activity",
              ].map((q) => (
                <button key={q} type="button" className="ai-panel__quick-chip" onClick={() => { setAnalyticsQuery(q); askAI(q); }}>
                  {q}
                </button>
              ))}
            </div>
            {renderResult({ showInsert: false, loadingText: "Analyzing trends..." })}
          </div>
        )}

        {/* ═══ Email Drafting ═══ */}
        {activeSection === "email" && (
          <div className="ai-panel__section">
            {renderBack()}
            <div className="ai-panel__section-title"><FiMail size={16} /> Draft Email</div>
            <input
              className="ai-panel__input"
              value={emailRecipient}
              onChange={(e) => setEmailRecipient(e.target.value)}
              placeholder="Recipient name or company (optional)"
            />
            <textarea
              className="ai-panel__textarea"
              value={emailPurpose}
              onChange={(e) => setEmailPurpose(e.target.value)}
              placeholder="What is the email about? e.g., 'Follow up on policy renewal for ABC Agency'"
              rows={3}
            />
            <button
              className="ai-panel__submit-btn"
              onClick={handleEmailDraft}
              disabled={loading || !emailPurpose.trim()}
              type="button"
            >
              {loading ? <FiLoader className="ai-panel__spinner" size={14} /> : <FiMail size={14} />}
              Draft Email
            </button>
            <p className="ai-panel__disclaimer">AI drafts only — cannot send emails directly.</p>
            {renderResult({ loadingText: "Drafting email..." })}
          </div>
        )}

        {/* ═══ LinkedIn ═══ */}
        {activeSection === "linkedin" && (
          <div className="ai-panel__section">
            {renderBack()}
            <div className="ai-panel__section-title"><FiLinkedin size={16} /> LinkedIn Message</div>
            <div className="ai-panel__type-selector">
              {LINKEDIN_TYPES.map((lt) => (
                <button
                  key={lt.key}
                  type="button"
                  className={`ai-panel__type-btn ${linkedinType === lt.key ? "ai-panel__type-btn--active" : ""}`}
                  onClick={() => setLinkedinType(lt.key)}
                >
                  <span>{lt.icon}</span>
                  <span>{lt.label}</span>
                </button>
              ))}
            </div>
            <input
              className="ai-panel__input"
              value={linkedinRecipient}
              onChange={(e) => setLinkedinRecipient(e.target.value)}
              placeholder="Recipient name and title"
            />
            <input
              className="ai-panel__input"
              value={linkedinCompany}
              onChange={(e) => setLinkedinCompany(e.target.value)}
              placeholder="Company (optional)"
            />
            <textarea
              className="ai-panel__textarea"
              value={linkedinPurpose}
              onChange={(e) => setLinkedinPurpose(e.target.value)}
              placeholder="Purpose or context (optional)"
              rows={2}
            />
            <button
              className="ai-panel__submit-btn"
              onClick={handleLinkedIn}
              disabled={loading || !linkedinRecipient.trim()}
              type="button"
            >
              {loading ? <FiLoader className="ai-panel__spinner" size={14} /> : <FiLinkedin size={14} />}
              Generate Message
            </button>
            {renderResult({ loadingText: "Crafting LinkedIn message..." })}
          </div>
        )}

        {/* ═══ Sales Coach ═══ */}
        {activeSection === "sales" && (
          <div className="ai-panel__section">
            {renderBack()}
            <div className="ai-panel__section-title"><FiTarget size={16} /> Sales Coach</div>
            <textarea
              className="ai-panel__textarea"
              value={salesTopic}
              onChange={(e) => setSalesTopic(e.target.value)}
              placeholder="e.g., 'How should I approach ABC Agency?', 'Cold call script for insurance broker'"
              rows={3}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSalesCoach();
                }
              }}
            />
            <button
              className="ai-panel__submit-btn"
              onClick={handleSalesCoach}
              disabled={loading || !salesTopic.trim()}
              type="button"
            >
              {loading ? <FiLoader className="ai-panel__spinner" size={14} /> : <FiTarget size={14} />}
              Get Guidance
            </button>
            <div className="ai-panel__quick-asks">
              {[
                "Cold call script for insurance broker",
                "Objection handling: 'We already have a vendor'",
                "Pipeline stage guidance for Prospect",
                "Email sequence for new prospects",
              ].map((q) => (
                <button key={q} type="button" className="ai-panel__quick-chip" onClick={() => { setSalesTopic(q); askAI(q); }}>
                  {q}
                </button>
              ))}
            </div>
            {renderResult({ loadingText: "Coaching..." })}
          </div>
        )}

        {/* ═══ BD Playbook ═══ */}
        {activeSection === "playbook" && (
          <div className="ai-panel__section">
            {renderBack()}
            <div className="ai-panel__section-title"><FiBookOpen size={16} /> BD Playbook Search</div>
            <textarea
              className="ai-panel__textarea"
              value={salesTopic}
              onChange={(e) => setSalesTopic(e.target.value)}
              placeholder="Search playbook for: email sequence, cold call tips, LinkedIn strategy..."
              rows={2}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handlePlaybook();
                }
              }}
            />
            <button
              className="ai-panel__submit-btn"
              onClick={handlePlaybook}
              disabled={loading || !salesTopic.trim()}
              type="button"
            >
              {loading ? <FiLoader className="ai-panel__spinner" size={14} /> : <FiBookOpen size={14} />}
              Search Playbook
            </button>
            <div className="ai-panel__quick-asks">
              {[
                "Email sequence for prospects",
                "LinkedIn outreach strategy",
                "Voicemail scripts",
                "Insurance 101 glossary",
                "Timing best practices",
              ].map((q) => (
                <button key={q} type="button" className="ai-panel__quick-chip" onClick={() => { setSalesTopic(q); }}>
                  {q}
                </button>
              ))}
            </div>
            {renderResult({ showInsert: false, loadingText: "Searching playbook..." })}
          </div>
        )}

        {/* ═══ Report Generator ═══ */}
        {activeSection === "report" && (
          <div className="ai-panel__section">
            {renderBack()}
            <div className="ai-panel__section-title"><FiFileText size={16} /> Generate Report</div>
            <div className="ai-panel__type-selector">
              {REPORT_TYPES.map((rt) => (
                <button
                  key={rt.key}
                  type="button"
                  className={`ai-panel__type-btn ${reportType === rt.key ? "ai-panel__type-btn--active" : ""}`}
                  onClick={() => setReportType(rt.key)}
                >
                  <span>{rt.icon}</span>
                  <span>{rt.label}</span>
                </button>
              ))}
            </div>
            <button
              className="ai-panel__submit-btn"
              onClick={() => handleReport(reportType)}
              disabled={loading}
              type="button"
            >
              {loading ? <FiLoader className="ai-panel__spinner" size={14} /> : <FiFileText size={14} />}
              Generate Report
            </button>
            {renderResult({ showInsert: false, loadingText: "Generating report..." })}
          </div>
        )}

        {/* ═══ Meeting Prep ═══ */}
        {activeSection === "meeting" && (
          <div className="ai-panel__section">
            {renderBack()}
            <div className="ai-panel__section-title"><FiCalendar size={16} /> Meeting Prep</div>
            <div className="ai-panel__type-selector">
              {MEETING_TYPES.map((mt) => (
                <button
                  key={mt.key}
                  type="button"
                  className={`ai-panel__type-btn ${meetingType === mt.key ? "ai-panel__type-btn--active" : ""}`}
                  onClick={() => setMeetingType(mt.key)}
                >
                  <span>{mt.icon}</span>
                  <span>{mt.label}</span>
                </button>
              ))}
            </div>
            <input
              className="ai-panel__input"
              value={meetingEntity}
              onChange={(e) => setMeetingEntity(e.target.value)}
              placeholder="Client or lead name (e.g., ABC Agency)"
            />
            <textarea
              className="ai-panel__textarea"
              value={meetingAgenda}
              onChange={(e) => setMeetingAgenda(e.target.value)}
              placeholder="Meeting agenda or notes (optional)"
              rows={2}
            />
            <button
              className="ai-panel__submit-btn"
              onClick={handleMeetingPrep}
              disabled={loading || (!meetingEntity.trim() && !meetingAgenda.trim())}
              type="button"
            >
              {loading ? <FiLoader className="ai-panel__spinner" size={14} /> : <FiCalendar size={14} />}
              Prepare Briefing
            </button>
            {renderResult({ showInsert: false, loadingText: "Preparing meeting briefing..." })}
          </div>
        )}

        {/* ═══ Ops Guide ═══ */}
        {activeSection === "ops" && (
          <div className="ai-panel__section">
            {renderBack()}
            <div className="ai-panel__section-title"><FiSettings size={16} /> Operations Guide</div>
            <textarea
              className="ai-panel__textarea"
              value={opsTopic}
              onChange={(e) => setOpsTopic(e.target.value)}
              placeholder="e.g., 'How to handle a policy endorsement?', 'SLA for renewal quotes'"
              rows={3}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleOps();
                }
              }}
            />
            <button
              className="ai-panel__submit-btn"
              onClick={handleOps}
              disabled={loading || !opsTopic.trim()}
              type="button"
            >
              {loading ? <FiLoader className="ai-panel__spinner" size={14} /> : <FiSettings size={14} />}
              Get Guidance
            </button>
            <div className="ai-panel__quick-asks">
              {[
                "New business quoting process",
                "COI issuance steps",
                "Policy endorsement workflow",
                "Workers comp process",
                "Renewal SLA timeline",
              ].map((q) => (
                <button key={q} type="button" className="ai-panel__quick-chip" onClick={() => { setOpsTopic(q); }}>
                  {q}
                </button>
              ))}
            </div>
            {renderResult({ loadingText: "Loading ops guidance..." })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AIAssistant;
